import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { type ButtonHTMLAttributes, forwardRef } from 'react'
import { cn } from '../lib/cn'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap text-[13px] font-medium transition-[color,background-color,border-color,opacity] duration-150 ease-out-expo focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        /* Primary CTA = pill ink (landing continuity). Other variants keep radius-md. */
        default:
          'rounded-full bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/80',
        secondary:
          'rounded-md bg-secondary text-secondary-foreground border border-border hover:bg-accent active:bg-accent/70',
        outline:
          'rounded-md border border-border bg-transparent text-foreground hover:bg-accent active:bg-accent/70',
        ghost: 'rounded-md text-foreground hover:bg-accent active:bg-accent/70',
        destructive:
          'rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90 active:bg-destructive/80',
      },
      size: {
        sm: 'h-8 px-3.5 text-xs',
        default: 'h-10 px-4',
        lg: 'h-11 px-5 text-sm',
        icon: 'h-10 w-10 rounded-md',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props} />
    )
  },
)
Button.displayName = 'Button'

export { buttonVariants }
